import { useEffect, useCallback, useRef } from 'react';
import { SplashScreen } from '@capacitor/splash-screen';
import { useNotifications } from 'features/notifications/hooks/useNotifications';
import { isPushNotificationsEnabled } from 'shared/initPushNotifications';
import { useOnboardingState } from 'state/useOnboardingState';
import { Capacitor } from '@capacitor/core';
import { useCriticalAlertsOnboarding } from 'hooks/useCriticalAlertsOnboarding';

// Capacitor keeps the splash visible until JS calls hide(); never block longer than this.
const SPLASH_HIDE_TIMEOUT_MS = 5_000;

export const useInitUserOnboard = (): void => {
  const {
    subscribedNotificationSettings: { isLoading, data },
  } = useNotifications({ retryQuery: false });
  const {
    onboardCompleted,
    setFirstAppOpen,
    setOnboardUser,
    setNotificationsEnabled,
  } = useOnboardingState();
  const hasOnboardingStateBeenChecked = useRef(false);
  const { shouldShowCriticalAlertsStep } = useCriticalAlertsOnboarding();

  const updateAppState = useCallback(
    async (userRequiresOnboarding: boolean) => {
      hasOnboardingStateBeenChecked.current = true;
      setFirstAppOpen();
      setOnboardUser(userRequiresOnboarding);
      // Hide the splash when we know if to show the onboarding flow or not
      await SplashScreen.hide();
    },
    [setOnboardUser, setFirstAppOpen],
  );

  const shouldOnboardUser = useCallback(async () => {
    if (hasOnboardingStateBeenChecked.current) return;

    const notificationsEnabled = await isPushNotificationsEnabled();
    if (notificationsEnabled) {
      // This is needed for a later step on the onboarding flow
      setNotificationsEnabled(notificationsEnabled);
    }

    const isAnyCountySelected = Boolean(data?.data?.length);

    // Check if we need to show critical alerts permission
    const requestCriticalAlerts =
      shouldShowCriticalAlertsStep(notificationsEnabled);

    // When a user opens the app for the first time, notifications won't be enabled
    // so "firstAppOpen === true" condition will also be met here
    if (
      (!notificationsEnabled ||
        !isAnyCountySelected ||
        requestCriticalAlerts) &&
      !onboardCompleted
    ) {
      await updateAppState(true);
      return;
    }

    await updateAppState(false);
  }, [
    updateAppState,
    data,
    onboardCompleted,
    setNotificationsEnabled,
    shouldShowCriticalAlertsStep,
  ]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || onboardCompleted) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      SplashScreen.hide().catch(() => undefined);
    }, SPLASH_HIDE_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [onboardCompleted]);

  useEffect(() => {
    // we never need to run this if the onboarding has completed once
    if (!Capacitor.isNativePlatform() || onboardCompleted) {
      return undefined;
    }

    if (!isLoading) {
      shouldOnboardUser();
      return undefined;
    }

    // Notifications queries can stall on slow networks; don't freeze the splash forever.
    const timeoutId = setTimeout(() => {
      shouldOnboardUser();
    }, SPLASH_HIDE_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [isLoading, shouldOnboardUser, onboardCompleted]);
};
